/**
 * EF-CLIENT-ONBOARDING-FRONTEND-V1: Client Password Reset Page
 * 
 * Routes:
 * - /client/reset-password (request form - enter email)
 * - /client/reset-password?token=...&user_id=... (confirm form - set new password)
 * 
 * Features:
 * - Step 1: Email input -> POST /api/client/password-reset-request
 * - Step 2: Password form (token-based) -> POST /api/client/password-reset-confirm
 */

import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "../../components/ui/card";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { 
  Loader2, Lock, Mail, AlertCircle, Building2, Eye, EyeOff, 
  CheckCircle2, KeyRound, ArrowLeft, XCircle, ShieldCheck
} from "lucide-react";
import { toast } from "sonner";
import { clientPasswordResetRequest, clientPasswordResetConfirm } from "../../lib/api";
import { useI18n } from "../../i18n/I18nProvider";

// Password validation rules (same as onboarding)
const PASSWORD_MIN_LENGTH = 8;

const validatePassword = (password) => {
  const checks = {
    length: password.length >= PASSWORD_MIN_LENGTH,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
  };
  const isValid = Object.values(checks).every(Boolean);
  return { checks, isValid };
};

export default function ClientResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useI18n();
  
  // Get token and user_id from URL (if present, show confirm form)
  const token = searchParams.get("token");
  const userId = searchParams.get("user_id");
  const hasToken = token && userId;
  
  // Request form state (Step 1)
  const [email, setEmail] = useState("");
  const [requestSent, setRequestSent] = useState(false);
  
  // Confirm form state (Step 2)
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [validationState, setValidationState] = useState({ checks: {}, isValid: false });
  const [resetComplete, setResetComplete] = useState(false);
  
  // Shared state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  
  // Update password validation on change
  useEffect(() => {
    setValidationState(validatePassword(password));
  }, [password]);

  // Handle Step 1: Request password reset
  const handleRequestReset = async (e) => {
    e.preventDefault();
    setError("");
    
    if (!email) {
      setError("Please enter your email address");
      return;
    }
    
    setLoading(true);
    console.log("[CLIENT PASSWORD RESET] Requesting reset for email");
    
    try {
      const response = await clientPasswordResetRequest(email);
      const data = response.data || response;
      
      if (data.success) {
        setRequestSent(true);
        console.log("[CLIENT PASSWORD RESET] Request sent successfully");
        toast.success("Reset link sent", {
          description: "If an account exists, you'll receive an email shortly.",
        });
      } else {
        // Generic success message for security (don't reveal if email exists)
        setRequestSent(true);
      }
    } catch (err) {
      console.error("[CLIENT PASSWORD RESET] Request error:", err);
      // For security, show success even on error (don't reveal if email exists)
      setRequestSent(true);
      toast.success("Reset link sent", {
        description: "If an account exists, you'll receive an email shortly.",
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle Step 2: Confirm new password
  const handleConfirmReset = async (e) => {
    e.preventDefault();
    setError("");
    
    // Validate passwords
    if (!validationState.isValid) {
      setError("Password does not meet requirements");
      return;
    }
    
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    
    if (!token || !userId) {
      setError("Invalid reset link");
      return;
    }
    
    setLoading(true);
    console.log("[CLIENT PASSWORD RESET] Confirming reset for user:", userId);
    
    try {
      const response = await clientPasswordResetConfirm(token, userId, password);
      const data = response.data || response;
      
      if (data.success) {
        setResetComplete(true);
        console.log("[CLIENT PASSWORD RESET] Password reset successful");
        toast.success(t("clientPortal:resetPassword.resetSuccess"), {
          description: "You can now sign in with your new password.",
        });
      } else {
        setError(data.message || "Failed to reset password");
        console.log("[CLIENT PASSWORD RESET] Failed:", data.message);
      }
    } catch (err) {
      console.error("[CLIENT PASSWORD RESET] Confirm error:", err);
      const errorMsg = err.response?.data?.detail || err.message || "Failed to reset password";
      setError(errorMsg);
      toast.error(t("clientPortal:resetPassword.resetFailed"), { description: errorMsg });
    } finally {
      setLoading(false);
    }
  };

  // STEP 2: Show confirm form if token is present
  if (hasToken) {
    // Show success state after reset complete
    if (resetComplete) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4">
          <Card className="w-full max-w-md relative bg-card/80 backdrop-blur-sm border-white/10" data-testid="reset-success-card">
            <CardHeader className="text-center">
              <div className="flex items-center justify-center mb-4">
                <div className="p-3 rounded-full bg-emerald-500/10 border border-emerald-500/20">
                  <CheckCircle2 className="h-8 w-8 text-emerald-400" />
                </div>
              </div>
              <CardTitle className="text-xl text-emerald-400">{t("clientPortal:resetPassword.resetComplete")}</CardTitle>
              <CardDescription className="text-muted-foreground">
                Your password has been successfully reset. You can now sign in with your new password.
              </CardDescription>
            </CardHeader>
            <CardFooter className="justify-center">
              <Button
                className="bg-sky-600 hover:bg-sky-500"
                onClick={() => navigate("/client/login")}
                data-testid="go-to-login-btn"
              >
                Sign In
              </Button>
            </CardFooter>
          </Card>
        </div>
      );
    }
    
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-1/2 -right-1/2 w-full h-full bg-sky-500/5 rounded-full blur-3xl" />
          <div className="absolute -bottom-1/2 -left-1/2 w-full h-full bg-amber-500/3 rounded-full blur-3xl" />
        </div>
        
        <Card className="w-full max-w-md relative bg-card/80 backdrop-blur-sm border-white/10" data-testid="reset-confirm-card">
          <CardHeader className="text-center pb-2">
            {/* Logo / Brand */}
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="p-2 rounded-lg bg-amber-500/10 border border-amber-500/20">
                <ShieldCheck className="h-8 w-8 text-amber-400" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold">{t("clientPortal:resetPassword.createNewPassword")}</CardTitle>
            <CardDescription>
              Enter a new password for your account
            </CardDescription>
          </CardHeader>
          
          <form onSubmit={handleConfirmReset}>
            <CardContent className="space-y-4">
              {/* Error Alert */}
              {error && (
                <Alert variant="destructive" className="border-red-500/50 bg-red-500/10" data-testid="reset-error">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              
              {/* Password Field */}
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm text-muted-foreground">
                  New Password
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Create a new password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10 bg-background/50"
                    disabled={loading}
                    data-testid="reset-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              
              {/* Password Requirements */}
              {password.length > 0 && (
                <div className="space-y-1 p-3 rounded-lg bg-background/30 border border-white/5" data-testid="password-requirements">
                  <p className="text-xs text-muted-foreground mb-2">{t("clientPortal:resetPassword.passwordRequirements")}</p>
                  <PasswordCheck 
                    label={`At least ${PASSWORD_MIN_LENGTH} characters`} 
                    passed={validationState.checks.length} 
                  />
                  <PasswordCheck 
                    label="One uppercase letter" 
                    passed={validationState.checks.uppercase} 
                  />
                  <PasswordCheck 
                    label="One lowercase letter" 
                    passed={validationState.checks.lowercase} 
                  />
                  <PasswordCheck 
                    label="One number" 
                    passed={validationState.checks.number} 
                  />
                </div>
              )}
              
              {/* Confirm Password Field */}
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-sm text-muted-foreground">
                  Confirm New Password
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="confirmPassword"
                    type={showConfirm ? "text" : "password"}
                    placeholder="Confirm your new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10 pr-10 bg-background/50"
                    disabled={loading}
                    data-testid="reset-confirm-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm(!showConfirm)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    tabIndex={-1}
                  >
                    {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {/* Match indicator */}
                {confirmPassword.length > 0 && (
                  <div className="flex items-center gap-1.5 text-xs mt-1">
                    {password === confirmPassword ? (
                      <>
                        <CheckCircle2 className="h-3 w-3 text-emerald-400" />
                        <span className="text-emerald-400">Passwords match</span>
                      </>
                    ) : (
                      <>
                        <XCircle className="h-3 w-3 text-red-400" />
                        <span className="text-red-400">Passwords do not match</span>
                      </>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
            
            <CardFooter className="flex flex-col gap-4">
              <Button
                type="submit"
                className="w-full bg-amber-600 hover:bg-amber-500"
                disabled={loading || !validationState.isValid || password !== confirmPassword}
                data-testid="reset-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Resetting password...
                  </>
                ) : (
                  <>
                    <KeyRound className="mr-2 h-4 w-4" />
                    Reset Password
                  </>
                )}
              </Button>
              
              <Link
                to="/client/login"
                className="text-xs text-center text-muted-foreground hover:text-foreground transition-colors flex items-center justify-center gap-1"
              >
                <ArrowLeft className="h-3 w-3" />
                Back to sign in
              </Link>
            </CardFooter>
          </form>
        </Card>
      </div>
    );
  }

  // STEP 1: Request form (no token in URL)
  
  // Show success state after request sent
  if (requestSent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4">
        <Card className="w-full max-w-md relative bg-card/80 backdrop-blur-sm border-white/10" data-testid="reset-sent-card">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center mb-4">
              <div className="p-3 rounded-full bg-sky-500/10 border border-sky-500/20">
                <Mail className="h-8 w-8 text-sky-400" />
              </div>
            </div>
            <CardTitle className="text-xl">{t("clientPortal:resetPassword.checkYourEmail")}</CardTitle>
            <CardDescription className="text-muted-foreground">
              If an account exists for <strong className="text-foreground">{email}</strong>, you'll receive a password reset link shortly.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-sm text-muted-foreground">
              Didn't receive an email? Check your spam folder or{" "}
              <button
                onClick={() => setRequestSent(false)}
                className="text-sky-400 hover:underline"
              >
                try again
              </button>
            </p>
          </CardContent>
          <CardFooter className="justify-center">
            <Link
              to="/client/login"
              className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to sign in
            </Link>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-1/2 -right-1/2 w-full h-full bg-sky-500/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-1/2 -left-1/2 w-full h-full bg-sky-500/3 rounded-full blur-3xl" />
      </div>
      
      <Card className="w-full max-w-md relative bg-card/80 backdrop-blur-sm border-white/10" data-testid="reset-request-card">
        <CardHeader className="text-center pb-2">
          {/* Logo / Brand */}
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="p-2 rounded-lg bg-sky-500/10 border border-sky-500/20">
              <KeyRound className="h-8 w-8 text-sky-400" />
            </div>
          </div>
          <CardTitle className="text-2xl font-bold">{t("clientPortal:resetPassword.title")}</CardTitle>
          <CardDescription>
            Enter your email address and we'll send you a link to reset your password
          </CardDescription>
        </CardHeader>
        
        <form onSubmit={handleRequestReset}>
          <CardContent className="space-y-4">
            {/* Error Alert */}
            {error && (
              <Alert variant="destructive" className="border-red-500/50 bg-red-500/10" data-testid="reset-request-error">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            
            {/* Email Field */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm text-muted-foreground">
                Email Address
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 bg-background/50"
                  disabled={loading}
                  data-testid="reset-email"
                />
              </div>
            </div>
          </CardContent>
          
          <CardFooter className="flex flex-col gap-4">
            <Button
              type="submit"
              className="w-full bg-sky-600 hover:bg-sky-500"
              disabled={loading || !email}
              data-testid="reset-request-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="mr-2 h-4 w-4" />
                  Send Reset Link
                </>
              )}
            </Button>
            
            <Link
              to="/client/login"
              className="text-sm text-center text-muted-foreground hover:text-foreground transition-colors flex items-center justify-center gap-1"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to sign in
            </Link>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}

// Password check item component
function PasswordCheck({ label, passed }) {
  return (
    <div className="flex items-center gap-2 text-xs">
      {passed ? (
        <CheckCircle2 className="h-3 w-3 text-emerald-400" />
      ) : (
        <XCircle className="h-3 w-3 text-muted-foreground" />
      )}
      <span className={passed ? "text-emerald-400" : "text-muted-foreground"}>
        {label}
      </span>
    </div>
  );
}
